import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Avatar,
  Tooltip,
  Chip,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  Slider,
  Card,
  CardMedia,
  CardContent,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Menu,
  ClickAwayListener,
  Popper,
  Grow,
  SelectChangeEvent,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  FormGroup,
  Switch,
  useMediaQuery,
  useTheme,
  Drawer,
  ListItemButton,
  ListItemIcon
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CropIcon from '@mui/icons-material/Crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CheckIcon from '@mui/icons-material/Check';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EditIcon from '@mui/icons-material/Edit';
import MenuIcon from '@mui/icons-material/Menu';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import DevicesIcon from '@mui/icons-material/Devices';
import PaymentIcon from '@mui/icons-material/Payment';
import MovieIcon from '@mui/icons-material/Movie';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { printTestPage } from '../qr-menu/components/PrintReceiptService';
import KitchenScreenSettings, { KitchenScreenSettingsData, OrderCardColorSettings } from '../qr-menu/components/KitchenScreenSettings';
import KioskCategoriesTab, { KioskCategory } from './components/KioskCategoriesTab';
import KioskProductsTab, { KioskProduct } from './components/KioskProductsTab';
import KioskCategoryProductTemplateBar from './components/KioskCategoryProductTemplateBar';
import KioskAdditionalSettingsTab, { KioskAdditionalSettings } from './components/KioskAdditionalSettingsTab';
import KioskReceiptSettingsTab from './components/KioskReceiptSettingsTab';
import { KioskReceiptDisplaySettings } from './components/KioskReceiptSettingsModal';
import KioskFiscalPrinterTab, { FiscalPrinterSettings } from './components/KioskFiscalPrinterTab';
import KioskDeviceTab from './components/KioskDeviceTab';
import KioskPaymentSettingsTab, { KioskPaymentSettings } from './components/KioskPaymentSettingsTab';
import KioskKitchenScreenTab from './components/KioskKitchenScreenTab';
import ImageLibrarySelectionModal from '../accounting/inventory/ImageLibrarySelectionModal';
import { mainApi, accountingApi } from '../../config/apiClient';
import {
  getPersonnel as getPersonnelSql,
  getScreenPermissionsByType,
  bulkSetScreenPermissions,
  deleteScreenPermission,
  getDepartments,
} from '../../services/migration/personnelDataService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

export interface KioskSettingsModalProps {
  open: boolean;
  onClose: () => void;
  kioskId: string | null;
  companyId: string | null;
  kioskName?: string;
  onImageUpload?: (productId: string, file: File) => Promise<string>;
}

// Ürün veri tipi
interface InventoryItem {
  id: string;
  name: string;
  categoryId?: string | null;
  warehouseId?: string | null;
  salePrice?: number;
  stockCode?: string;
  barcode?: string;
  imageUrl?: string;
  description?: string;
}

// Menü veri tipi
interface Menu {
  id: string;
  name: string;
  products?: string[];
  imageUrl?: string;
}

// Personel veri tipi (mutfak erişimi için)
interface Personnel {
  id: string;
  name: string;
  timestamp?: number;
  departmentId?: string | null;
  departmentName?: string;
}

// Mutfak personeli izinleri tipi
interface KitchenPersonnelPermissions {
  accessKitchenScreen: boolean; // Mutfak ekranına erişim yetkisi
  viewAllOrders: boolean; // Tüm siparişleri görme yetkisi
  updateOrderStatus: boolean; // Sipariş durumunu güncelleme yetkisi
  cancelOrders: boolean; // Sipariş iptal etme yetkisi
  showPrintButton: boolean; // Yazdırma butonu göster/gizle
  listReadyOrders: boolean; // Hazır siparişleri listele (false ise 5 saniyede kaldır)
  waiterMode: boolean; // Garson modu (true ise "Garson Ekranı" başlığı)
  autoPrint: boolean; // Otomatik yazdırma (yeni siparişler geldiğinde otomatik yazdır)
  showPrices: boolean; // Fiyatları göster (false ise hiçbir yerde fiyat görünmez)
}

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

// Kategori tipi
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

// Adisyon görüntü ayarları
interface ReceiptDisplaySettings {
  companyName: string;
  companyAddress: string;
  taxNumber: string;
  showReceiptHeader: boolean;
  showVat: boolean;
  showDiscountInfo: boolean;
  showCashierName: boolean;
  showReceiptNumber: boolean;
  receiptNumberResetValue: number;
  resetAtEndOfDay: boolean;
  printCopyCount: number;
  productGrouping: string;
  standardFooterNote: string;
  thankYouMessage: string;
  receiptWidth: number;
  characterLength: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  headerType: 'text' | 'logo';
  logoUrl: string;
}

// Reklam veri tipi
// 🎬 Video reklam desteği:
//  - mediaType: 'image' (default, geriye uyumlu) | 'video'
//  - videoUrl: mediaType === 'video' ise dolu olur (Firebase Storage URL)
//  - imageUrl: image için ana içerik; video için poster/thumbnail (opsiyonel)
//  - duration: image için süre. Video için yine girilebilir ama kiosk
//    video bittiğinde sıradaki reklama geçer (video süresi = oynatma süresi).
interface Advertisement {
  id: string;
  imageUrl: string;
  order: number;
  duration: number; // milisaniye cinsinden
  mediaType?: 'image' | 'video';
  videoUrl?: string;
}

// Ana ekran ayarları veri tipi
interface HomeScreenSettings {
  advertisements?: Advertisement[];
  orderButton?: {
    text?: string;
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: number;
    fontSize?: number;
    height?: number;
  };
  layout?: {
    adAreaHeightPercent?: number; // Varsayılan: 67
    buttonAreaHeightPercent?: number; // Varsayılan: 33
  };
}

// Sipariş ekranı ayarları veri tipi
interface OrderScreenSettings {
  topBannerAdvertisements?: Advertisement[]; // Üst banner reklamları
}

// Kiosk Ayarları veri tipi
interface KioskSettings {
  products?: string[];
  template?: string;
  menus?: { [menuId: string]: Menu };
  // Ana ekran ayarları
  homeScreen?: HomeScreenSettings;
  // Sipariş ekranı ayarları
  orderScreen?: OrderScreenSettings;
  // Kategoriler
  categories?: KioskCategory[];
  // Ürünler (kategori ID'sine göre gruplandırılmış)
  kioskProducts?: { [categoryId: string]: KioskProduct[] };
  // Ek ayarlar
  additionalSettings?: KioskAdditionalSettings;
  appearance?: {
    template?: string;
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
    showPrices?: boolean;
    showImages?: boolean;
    showDescriptions?: boolean;
    cardStyle?: 'standard' | 'compact' | 'full-width' | 'minimal';
    showLogo?: boolean;
    currencySymbol?: string;
    menuLogoUrl?: string;
    allowOrderNotes?: boolean;
    // Sepete ekle butonu ayarları
    addToCartButton?: {
      enabled?: boolean;
      text?: string;
      style?: 'filled' | 'outlined' | 'text';
      size?: 'small' | 'medium' | 'large';
      position?: 'bottom' | 'overlay' | 'side';
      backgroundColor?: string;
      textColor?: string;
      borderColor?: string;
      borderRadius?: number;
      showIcon?: boolean;
      animation?: 'none' | 'pulse' | 'bounce';
    };
  };
  // Mutfak personeli ayarları
  kitchenPersonnel?: {
    assignedPersonnelIds?: string[];
    personnelPermissions?: { [personnelId: string]: KitchenPersonnelPermissions };
  };
  // Yazdırma ayarları
  printingSettings?: {
    printingMode?: 'single' | 'category';
    defaultReceiptPrinter?: DefinedPrinter | null;
    definedPrinters?: DefinedPrinter[];
    categoryPrinterAssignments?: { [categoryId: string]: string | null };
    receiptDisplaySettings?: ReceiptDisplaySettings;
  };
  // Mutfak ekranı ayarları
  kitchenScreenSettings?: KitchenScreenSettingsData;
  // Kiosk Adisyon Ayarları
  kioskReceiptSettings?: KioskReceiptDisplaySettings;
  // Yazarkasa Ayarları
  fiscalPrinterSettings?: FiscalPrinterSettings;
  // Ödeme Ayarları
  paymentSettings?: KioskPaymentSettings;
}

const KioskSettingsModal: React.FC<KioskSettingsModalProps> = ({
  open,
  onClose,
  kioskId,
  companyId,
  kioskName,
  onImageUpload
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { t } = useTranslation('salesSystem');
  const [mobileTabDrawerOpen, setMobileTabDrawerOpen] = useState(false);
  
  // Tab tanımları
  const tabs = [
    { id: 0, label: t('kioskSettings.tabs.advertisements'), icon: <CampaignIcon /> },
    { id: 1, label: t('kioskSettings.tabs.categories'), icon: <CategoryIcon /> },
    { id: 2, label: t('kioskSettings.tabs.products'), icon: <InventoryIcon /> },
    { id: 3, label: t('kioskSettings.tabs.additionalSettings'), icon: <TuneIcon /> },
    { id: 4, label: t('kioskSettings.tabs.personnel'), icon: <PeopleIcon /> },
    { id: 5, label: t('kioskSettings.tabs.receiptSettings'), icon: <ReceiptIcon /> },
    { id: 6, label: t('kioskSettings.tabs.fiscalPrinter'), icon: <PrintIcon /> },
    { id: 7, label: t('kioskSettings.tabs.deviceManagement'), icon: <DevicesIcon /> },
    { id: 8, label: t('kioskSettings.tabs.paymentSettings'), icon: <PaymentIcon /> },
    { id: 9, label: t('kioskSettings.tabs.kitchenScreen'), icon: <RestaurantMenuIcon /> },
  ];
  
  const [settings, setSettings] = useState<KioskSettings | null>(null);
  const [initialSettings, setInitialSettings] = useState<KioskSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [allInventoryItems, setAllInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Menü özellikleri için state'ler
  const [menus, setMenus] = useState<Menu[]>([]);
  const [activeMenuTab, setActiveMenuTab] = useState<number>(0);
  const [menuDialogOpen, setMenuDialogOpen] = useState<boolean>(false);
  const [newMenuName, setNewMenuName] = useState<string>('');
  const [selectedProductsForMenu, setSelectedProductsForMenu] = useState<{ [menuId: string]: string[] }>({});
  const [initialSelectedProductsForMenu, setInitialSelectedProductsForMenu] = useState<{ [menuId: string]: string[] }>({});
  const [currentMenuForProducts, setCurrentMenuForProducts] = useState<string | null>(null);
  
  // Her menü için ayrı sayfalama state'leri
  const [menuPagination, setMenuPagination] = useState<{ [menuId: string]: { page: number; rowsPerPage: number } }>({});
  
  // Resim Kütüphanesi modalı için state'ler (QR Menu gibi)
  const [imageLibraryModalOpen, setImageLibraryModalOpen] = useState(false);
  const [currentProductIdForImage, setCurrentProductIdForImage] = useState<string | null>(null);
  const [currentImageUploadCallback, setCurrentImageUploadCallback] = useState<((imageUrl: string) => void) | null>(null);
  
  // Kiosk adı değiştirme için state
  const [currentKioskName, setCurrentKioskName] = useState<string>('');
  
  // ✅ Kiosk şube bilgisi (şube fiyatları için)
  const [kioskBranchId, setKioskBranchId] = useState<string | null>(null);

  // ✅ Kiosk'a atanmış QR menü FirebaseId (Mutfak Ekranı tabı)
  const [kioskQrMenuFirebaseId, setKioskQrMenuFirebaseId] = useState<string | null>(null);

  // Personel ayarları için state'ler
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState<Personnel[]>([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<{ [personnelId: string]: KitchenPersonnelPermissions }>({});
  const [personnelLoading, setPersonnelLoading] = useState(false);
  
  // Departman ayarları için state'ler
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<{ id: string; name: string } | null>(null);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Yazdırma ayarları için state'ler
  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('single');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);
  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);
  const [receiptDisplaySettings, setReceiptDisplaySettings] = useState<ReceiptDisplaySettings>({
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    showReceiptHeader: true,
    showVat: true,
    showDiscountInfo: false,
    showCashierName: false, // Kasiyer bölümü kapalı
    showReceiptNumber: false, // Fiş sırası kapalı
    receiptNumberResetValue: 999999,
    resetAtEndOfDay: true,
    printCopyCount: 1,
    productGrouping: 'category',
    standardFooterNote: 'Afiyet olsun!',
    thankYouMessage: 'Tekrar bekleriz.',
    receiptWidth: 80,
    characterLength: 32,
    marginTop: 2,
    marginBottom: 5,
    marginLeft: 5,
    marginRight: 5,
    headerType: 'text', // Varsayılan olarak metin başlık
    logoUrl: ''
  });
  
  // Receipt Settings Modal state
  const [receiptSettingsModalOpen, setReceiptSettingsModalOpen] = useState(false);

  // Kiosk Adisyon Ayarları için state
  const [kioskReceiptSettings, setKioskReceiptSettings] = useState<KioskReceiptDisplaySettings>({
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    companyPhone: '', // ✅ YENİ ALAN
    companyWebsite: '', // ✅ YENİ ALAN
    mersisNumber: '', // ✅ YENİ ALAN
    showReceiptHeader: true,
    showVat: true,
    showDiscountInfo: false,
    showCashierName: false,
    showReceiptNumber: false,
    receiptNumberResetValue: 999999,
    resetAtEndOfDay: true,
    printCopyCount: 1,
    productGrouping: 'category',
    standardFooterNote: 'Afiyet olsun!',
    thankYouMessage: 'Bizi tercih ettiğiniz için teşekkür ederiz.',
    receiptWidth: 80,
    characterLength: 32,
    marginTop: 2,
    marginBottom: 5,
    marginLeft: 5,
    marginRight: 5,
    headerType: 'text',
    logoUrl: ''
  });

  // Mutfak ekranı ayarları için state'ler
  const [kitchenScreenSettings, setKitchenScreenSettings] = useState<KitchenScreenSettingsData>({
    orderCardColors: {
      pending: {
        '0-5': { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
        '6-15': { backgroundColor: '#fff3e0', borderColor: '#ff9800', timeColor: '#f57c00' },
        '16-25': { backgroundColor: '#fce4ec', borderColor: '#e91e63', timeColor: '#c2185b' },
        '25+': { backgroundColor: '#ffebee', borderColor: '#f44336', timeColor: '#d32f2f' }
      },
      preparing: { backgroundColor: '#fff8e1', borderColor: '#ffc107', timeColor: '#f9a825' },
      ready: { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
      delivered: { backgroundColor: '#f3e5f5', borderColor: '#9c27b0', timeColor: '#7b1fa2' },
      cancelled: { backgroundColor: '#fafafa', borderColor: '#9e9e9e', timeColor: '#616161' }
    },
    timeRanges: {
      range1: 5,   // 0-5 dakika
      range2: 15,  // 6-15 dakika  
      range3: 25   // 16-25 dakika
    }
  });

  // Yazarkasa ayarları için state
  const [fiscalPrinterSettings, setFiscalPrinterSettings] = useState<FiscalPrinterSettings>({
    fiscalPrinterType: 'huginT300',
    huginT300BaseUrl: '',
    isEnabled: false,
  });

  // Ödeme ayarları state
  const [paymentSettings, setPaymentSettings] = useState<KioskPaymentSettings>({});

  // Ana ekran (Home Screen) ayarları için state'ler
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [orderButtonSettings, setOrderButtonSettings] = useState({
    text: t('kioskSettings.advertisements.orderButtonDefaultText'),
    backgroundColor: '#FF5722',
    textColor: '#FFFFFF',
    borderRadius: 12,
    fontSize: 24,
    height: 80
  });
  const [layoutSettings, setLayoutSettings] = useState({
    adAreaHeightPercent: 67,
    buttonAreaHeightPercent: 33
  });
  
  // Sipariş ekranı banner reklamları için state'ler
  const [orderScreenBannerAds, setOrderScreenBannerAds] = useState<Advertisement[]>([]);
  
  // Kategoriler için state'ler
  const [categories, setCategories] = useState<KioskCategory[]>([]);
  
  // Ürünler için state'ler (kategori ID'sine göre gruplandırılmış)
  const [kioskProducts, setKioskProducts] = useState<{ [categoryId: string]: KioskProduct[] }>({});
  
  // Ek ayarlar için state'ler
  const [additionalSettings, setAdditionalSettings] = useState<KioskAdditionalSettings>({});
  
  // Reklam ekleme/düzenleme için state'ler
  const [adSettingsDialogOpen, setAdSettingsDialogOpen] = useState(false);
  const [currentAdDuration, setCurrentAdDuration] = useState(5); // saniye
  const [editingAdId, setEditingAdId] = useState<string | null>(null);
  const [editingAdContext, setEditingAdContext] = useState<'home' | 'order'>('home'); // Hangi ekran için düzenleme yapılıyor
  
  // Renk seçici state'leri
  const [bgColorPickerOpen, setBgColorPickerOpen] = useState(false);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [bgColorAnchorEl, setBgColorAnchorEl] = useState<HTMLElement | null>(null);
  const [textColorAnchorEl, setTextColorAnchorEl] = useState<HTMLElement | null>(null);

  // Yaygın yazı tipleri listesi
  const fontFamilies = [
    'Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Trebuchet MS',
    'Times New Roman', 'Georgia', 'Garamond', 'Courier New', 'Brush Script MT',
    'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Raleway', 'Poppins',
    'Source Sans Pro', 'Ubuntu', 'Playfair Display', 'Merriweather'
  ];
  const [selectedFont, setSelectedFont] = useState<string>('Roboto');

  // Departman verilerini SQL'den yükleme (gts_hr_api / departmentDataService)
  useEffect(() => {
    if (open && companyId) {
      const fetchDepartmentsFromSql = async () => {
        setDepartmentsLoading(true);
        try {
          const sqlDepartments = await getDepartments(companyId);
          const fetchedDepartments = sqlDepartments
            .filter((d) => d && d.name)
            .map((d) => ({ id: String(d.id), name: d.name }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setDepartments(fetchedDepartments);
          console.log(`✅ SQL'den ${fetchedDepartments.length} departman yüklendi`);
        } catch (error) {
          console.error('❌ SQL departman yükleme hatası:', error);
          setDepartments([]);
        } finally {
          setDepartmentsLoading(false);
        }
      };

      fetchDepartmentsFromSql();
    }
  }, [open, companyId]);

  // Personel verilerini Firebase'den yükleme
  useEffect(() => {
    if (open && companyId) {
      const fetchPersonnelFromFirebase = async () => {
        setPersonnelLoading(true);
        try {
          // Faz 3: personel listesi SQL'den (personnelDataService), departman adlari SQL departmanlarindan.
          const [sqlPersonnel, sqlDepartments] = await Promise.all([
            getPersonnelSql(companyId),
            getDepartments(companyId).catch(() => []),
          ]);
          const deptNameById: { [id: string]: string } = {};
          (sqlDepartments || []).forEach((d: any) => {
            if (d && d.id != null) deptNameById[String(d.id)] = d.name || '';
          });

          const fetchedAllPersonnel: Personnel[] = (sqlPersonnel || []).map((p: any) => {
            const deptId = p.departmentId != null ? String(p.departmentId) : null;
            return {
              id: p.id, // Firebase UID
              name: p.name || 'İsimsiz Personel',
              timestamp: p.timestamp || Date.now(),
              departmentId: deptId,
              departmentName: deptId && deptNameById[deptId] ? deptNameById[deptId] : '',
            } as Personnel;
          });

          fetchedAllPersonnel.sort((a, b) => a.name.localeCompare(b.name));
          setAllPersonnel(fetchedAllPersonnel);
          console.log(`✅ SQL'den ${fetchedAllPersonnel.length} personel yüklendi`);
        } catch (error) {
          console.error('❌ Personel yükleme hatası:', error);
          setAllPersonnel([]);
        } finally {
          setPersonnelLoading(false);
        }
      };
      
      fetchPersonnelFromFirebase();
    }
  }, [open, companyId]);

  // Kiosk personel ayarlarını Firebase'den yükleme
  useEffect(() => {
    if (open && companyId && kioskId && allPersonnel.length > 0) {
      const loadKioskPersonnelSettings = async () => {
        try {
          console.log('🔄 Firebase\'den kiosk personel ayarları yükleniyor...');
          
          const loadedPersonnelIds: string[] = [];
          const loadedPermissionsMap: { [personnelId: string]: KitchenPersonnelPermissions } = {};
          
          // Kiosk yetkileri SADECE SQL'den (SystemUserScreenPermissions screenType='kiosks').
          await Promise.all(allPersonnel.map(async (personnel) => {
            let permissions: any = null;
            try {
              const rows = await getScreenPermissionsByType(personnel.id, 'kiosks');
              const row = rows.find(r => String(r.screenId) === String(kioskId) && r.isEnabled);
              if (row && row.settings) permissions = row.settings;
            } catch (e) {
              console.warn('[KioskSettingsModal] SQL kiosk yetki okuma hatasi:', e);
            }
            if (permissions) {
              loadedPersonnelIds.push(personnel.id);
              loadedPermissionsMap[personnel.id] = permissions;
            }
          }));
          
          // State'leri güncelle
          setSelectedPersonnelIds(loadedPersonnelIds);
          setPersonnelPermissionsMap(loadedPermissionsMap);
          
          // InitialSettings'i de güncelle ki değişiklik kontrolü doğru çalışsın
          setInitialSettings(prev => ({
            ...prev,
            kitchenPersonnel: {
              assignedPersonnelIds: loadedPersonnelIds,
              personnelPermissions: loadedPermissionsMap
            }
          }));
          
          console.log(`✅ Firebase'den ${loadedPersonnelIds.length} kiosk personeli yüklendi`);
          
          // İlk personeli otomatik seç (eğer varsa)
          if (loadedPersonnelIds.length > 0) {
            setSelectedPersonnelForPermissions(loadedPersonnelIds[0]);
          }
          
        } catch (error) {
          console.error('❌ Firebase kiosk personel ayarları yükleme hatası:', error);
        }
      };
      
      loadKioskPersonnelSettings();
    }
  }, [open, companyId, kioskId, allPersonnel]);

  // Kiosk ayarlarını yükleme
  useEffect(() => {
    if (open && kioskId && companyId) {
      setLoading(true);
      setError(null);
      
      const fetchData = async () => {
        try {
          console.log(`🔄 SQL'den kiosk bilgileri yükleniyor: /api/Kiosk/${kioskId}`);
          
          let loadedSettings: KioskSettings = {};
          
          try {
            const kioskResponse = await mainApi.get(`/api/Kiosk/${kioskId}`);
            const kioskResult = kioskResponse.data;
            console.log('✅ Kiosk bilgileri alındı:', kioskResult);
            
            if (kioskResult.success && kioskResult.data) {
              const kioskData = kioskResult.data;
              
              if (kioskData.branchId) {
                setKioskBranchId(kioskData.branchId);
                console.log('✅ Kiosk şube bilgisi alındı:', { branchId: kioskData.branchId, branchName: kioskData.branchName });
              }

              setKioskQrMenuFirebaseId(kioskData.qrMenuFirebaseId ?? null);
              console.log('✅ Kiosk QR Menü ataması:', kioskData.qrMenuFirebaseId ?? '<NULL (fallback)>');
              
              if (kioskData.kioskSettings) {
                try {
                  loadedSettings = JSON.parse(kioskData.kioskSettings);
                  console.log('✅ Kiosk ayarları parse edildi:', loadedSettings);
                } catch (parseError) {
                  console.error('❌ KioskSettings parse hatası:', parseError);
                }
              }
            }
          } catch (kioskError) {
            console.error('❌ Kiosk bilgileri yükleme hatası:', kioskError);
          }
          
          setSettings(loadedSettings);
          setCurrentKioskName(kioskName || '');
          
          // ✅ kioskReceiptSettings'i yükle
          if (loadedSettings.kioskReceiptSettings) {
            setKioskReceiptSettings(loadedSettings.kioskReceiptSettings);
            console.log(`✅ [KIOSK RECEIPT] Kiosk adisyon ayarları yüklendi:`, loadedSettings.kioskReceiptSettings);
          }
          
          // ✅ initialSettings'i kioskReceiptSettings ile birlikte set et
          setInitialSettings({
            ...JSON.parse(JSON.stringify(loadedSettings)),
            kioskReceiptSettings: loadedSettings.kioskReceiptSettings || kioskReceiptSettings
          });
          
          // Yazı tipi ayarını yükle
          if (loadedSettings.appearance?.fontFamily) {
            setSelectedFont(loadedSettings.appearance.fontFamily);
          }
          
          // Personel ayarları Firebase'den yüklenecek, burada sadece placeholder
          // Firebase'den yükleme işlemi ayrı useEffect'te yapılıyor
          
          // Yazdırma ayarlarını yükle
          if (loadedSettings.printingSettings) {
            setPrintingMode(loadedSettings.printingSettings.printingMode || 'single');
            setDefaultReceiptPrinter(loadedSettings.printingSettings.defaultReceiptPrinter || null);
            setDefinedPrinters(loadedSettings.printingSettings.definedPrinters || []);
            setCategoryPrinterAssignments(loadedSettings.printingSettings.categoryPrinterAssignments || {});
            if (loadedSettings.printingSettings.receiptDisplaySettings) {
              setReceiptDisplaySettings(loadedSettings.printingSettings.receiptDisplaySettings);
            }
            console.log(`✅ Yazdırma ayarları yüklendi`);
          }
          
          // Mutfak ekranı ayarlarını yükle
          if (loadedSettings.kitchenScreenSettings) {
            setKitchenScreenSettings(loadedSettings.kitchenScreenSettings);
            console.log(`✅ Mutfak ekranı ayarları yüklendi`);
          }
          
          // Yazarkasa ayarlarını yükle
          if (loadedSettings.fiscalPrinterSettings) {
            setFiscalPrinterSettings(loadedSettings.fiscalPrinterSettings);
            console.log(`✅ Yazarkasa ayarları yüklendi:`, loadedSettings.fiscalPrinterSettings);
          }

          // Ödeme ayarlarını yükle
          if (loadedSettings.paymentSettings) {
            setPaymentSettings(loadedSettings.paymentSettings);
            console.log(`✅ Ödeme ayarları yüklendi:`, loadedSettings.paymentSettings);
          }
          
          // Ana ekran ayarlarını yükle
          if (loadedSettings.homeScreen) {
            if (loadedSettings.homeScreen.advertisements) {
              setAdvertisements(loadedSettings.homeScreen.advertisements);
              console.log(`✅ ${loadedSettings.homeScreen.advertisements.length} reklam yüklendi`);
            }
            if (loadedSettings.homeScreen.orderButton) {
              setOrderButtonSettings({
                text: loadedSettings.homeScreen.orderButton.text || t('kioskSettings.advertisements.orderButtonDefaultText'),
                backgroundColor: loadedSettings.homeScreen.orderButton.backgroundColor || '#FF5722',
                textColor: loadedSettings.homeScreen.orderButton.textColor || '#FFFFFF',
                borderRadius: loadedSettings.homeScreen.orderButton.borderRadius || 12,
                fontSize: loadedSettings.homeScreen.orderButton.fontSize || 24,
                height: loadedSettings.homeScreen.orderButton.height || 80
              });
              console.log(`✅ Sipariş Ver butonu ayarları yüklendi`);
            }
            if (loadedSettings.homeScreen.layout) {
              setLayoutSettings({
                adAreaHeightPercent: loadedSettings.homeScreen.layout.adAreaHeightPercent || 67,
                buttonAreaHeightPercent: loadedSettings.homeScreen.layout.buttonAreaHeightPercent || 33
              });
              console.log(`✅ Layout ayarları yüklendi`);
            }
          }
          
          // Sipariş ekranı ayarlarını yükle
          if (loadedSettings.orderScreen?.topBannerAdvertisements) {
            setOrderScreenBannerAds(loadedSettings.orderScreen.topBannerAdvertisements);
            console.log(`✅ ${loadedSettings.orderScreen.topBannerAdvertisements.length} sipariş ekranı banner reklamı yüklendi`);
          }
          
          // Kategorileri yükle
          if (loadedSettings.categories) {
            setCategories(loadedSettings.categories);
            console.log(`✅ ${loadedSettings.categories.length} kategori yüklendi`);
          }
          
          // Ürünleri yükle
          if (loadedSettings.kioskProducts) {
            setKioskProducts(loadedSettings.kioskProducts);
            const totalProducts = Object.values(loadedSettings.kioskProducts).reduce((acc, products) => acc + products.length, 0);
            console.log(`✅ ${totalProducts} kiosk ürünü yüklendi`);
          }
          
          // Ek ayarları yükle
          if (loadedSettings.additionalSettings) {
            setAdditionalSettings(loadedSettings.additionalSettings);
            console.log(`✅ Ek ayarlar yüklendi:`, loadedSettings.additionalSettings);
          }
          
          try {
            console.log(`🔄 Kiosk API'ye bağlanılıyor: /api/QRMenu/company/${companyId}/stocks`);
            const response = await mainApi.get(`/api/QRMenu/stocks`);
            const result = response.data;
            if (result.success && result.data) {
              const itemsList: InventoryItem[] = result.data.map((item: any) => ({
                id: item.id,
                name: item.name || 'İsimsiz Ürün',
                categoryId: item.categoryName || null,
                warehouseId: null,
                salePrice: item.salePrice || 0,
                stockCode: item.stockCode || '-',
                barcode: item.barcode || '-',
                imageUrl: item.imageUrl || undefined,
                description: item.description || ''
              }));
              setAllInventoryItems(itemsList);
              console.log(`✅ Kiosk API'den ${itemsList.length} ürün yüklendi`);
            } else {
              console.warn('❌ API\'den ürün listesi alınamadı:', result.message);
              setAllInventoryItems([]);
            }
          } catch (apiError) {
            console.error('❌ API bağlantı hatası:', apiError);
            setAllInventoryItems([]);
          }
        } catch (err) {
          console.error("Kiosk ayarları yüklenirken hata:", err);
          setError(t('kioskSettingsModal.errors.dataLoadFailed'));
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [open, kioskId, companyId]);

  // Tab değişikliği
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (isMobile) {
      setMobileTabDrawerOpen(false);
    }
  };

  // Değişiklik olup olmadığını kontrol et
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings) || 
                     currentKioskName.trim() !== (kioskName || '') ||
                     // Personel seçimi değişikliklerini kontrol et
                     JSON.stringify(selectedPersonnelIds) !== JSON.stringify(initialSettings?.kitchenPersonnel?.assignedPersonnelIds || []) ||
                     JSON.stringify(personnelPermissionsMap) !== JSON.stringify(initialSettings?.kitchenPersonnel?.personnelPermissions || {}) ||
                     // Ana ekran ayarları değişikliklerini kontrol et
                     JSON.stringify(advertisements) !== JSON.stringify(initialSettings?.homeScreen?.advertisements || []) ||
                     JSON.stringify(orderButtonSettings) !== JSON.stringify(initialSettings?.homeScreen?.orderButton || {}) ||
                     JSON.stringify(layoutSettings) !== JSON.stringify(initialSettings?.homeScreen?.layout || {}) ||
                     // Sipariş ekranı ayarları değişikliklerini kontrol et
                     JSON.stringify(orderScreenBannerAds) !== JSON.stringify(initialSettings?.orderScreen?.topBannerAdvertisements || []) ||
                     // Kategoriler değişikliklerini kontrol et
                     JSON.stringify(categories) !== JSON.stringify(initialSettings?.categories || []) ||
                     // Ürünler değişikliklerini kontrol et
                     JSON.stringify(kioskProducts) !== JSON.stringify(initialSettings?.kioskProducts || {}) ||
                     // Ek ayarlar değişikliklerini kontrol et
                     JSON.stringify(additionalSettings) !== JSON.stringify(initialSettings?.additionalSettings || {}) ||
                     // Kiosk adisyon ayarları değişikliklerini kontrol et
                     JSON.stringify(kioskReceiptSettings) !== JSON.stringify(initialSettings?.kioskReceiptSettings || {}) ||
                     // Yazarkasa ayarları değişikliklerini kontrol et
                     JSON.stringify(fiscalPrinterSettings) !== JSON.stringify(initialSettings?.fiscalPrinterSettings || {}) ||
                     // Ödeme ayarları değişikliklerini kontrol et
                     JSON.stringify(paymentSettings) !== JSON.stringify(initialSettings?.paymentSettings || {});

  // Ayarları kaydetme
  const handleSave = async () => {
    if (!kioskId || !companyId) {
      setError(t('kioskSettingsModal.errors.kioskOrCompanyIdNotFound'));
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      console.log('🔄 [KIOSK SETTINGS] Ayarlar kaydediliyor...');
      
      // Kiosk ayarlarını hazırla
      const updatedSettings = {
        ...(settings || {}),
        // Ana ekran ayarları
        homeScreen: {
          advertisements: advertisements,
          orderButton: orderButtonSettings,
          layout: layoutSettings
        },
        // Sipariş ekranı ayarları
        orderScreen: {
          topBannerAdvertisements: orderScreenBannerAds
        },
        // Kategoriler
        categories: categories,
        // Ürünler
        kioskProducts: kioskProducts,
        // Ek ayarlar
        additionalSettings: additionalSettings,
        // Mutfak personeli ayarlarını ekle
        kitchenPersonnel: {
          assignedPersonnelIds: selectedPersonnelIds,
          personnelPermissions: personnelPermissionsMap
        },
        // Yazdırma ayarlarını ekle
        printingSettings: {
          printingMode: printingMode,
          defaultReceiptPrinter: defaultReceiptPrinter,
          definedPrinters: definedPrinters,
          categoryPrinterAssignments: categoryPrinterAssignments,
          receiptDisplaySettings: receiptDisplaySettings
        },
        // Mutfak ekranı ayarlarını ekle
        kitchenScreenSettings: kitchenScreenSettings,
        // Kiosk Adisyon Ayarlarını ekle
        kioskReceiptSettings: kioskReceiptSettings,
        // Yazarkasa ayarlarını ekle
        fiscalPrinterSettings: fiscalPrinterSettings,
        // Ödeme ayarlarını ekle
        paymentSettings: paymentSettings
      };
      
      console.log('📋 [KIOSK SETTINGS] Kaydedilecek ayarlar:', {
        settings: updatedSettings,
        personnelSettings: {
          assignedPersonnelIds: selectedPersonnelIds,
          personnelPermissions: personnelPermissionsMap
        },
        kioskName: currentKioskName,
        kioskReceiptSettings: kioskReceiptSettings // ✅ Ekstra log
      });
      
      // ✅ kioskReceiptSettings kontrolü
      console.log('🔍 [KIOSK SETTINGS] kioskReceiptSettings kontrolü:', {
        exists: !!updatedSettings.kioskReceiptSettings,
        value: updatedSettings.kioskReceiptSettings
      });
      
      // Silinen personelleri tespit et ve Firebase'den sil
      const initialPersonnelIds = initialSettings?.kitchenPersonnel?.assignedPersonnelIds || [];
      const removedPersonnelIds = initialPersonnelIds.filter(id => !selectedPersonnelIds.includes(id));
      
      if (removedPersonnelIds.length > 0) {
        console.log('🗑️ Silinen personeller tespit edildi:', removedPersonnelIds);
        await removeKioskPersonnelFromFirebase(removedPersonnelIds);
        await removeKioskPersonnelFromSQL(removedPersonnelIds);
      }
      
      // Firebase'e kiosk personeli yetkilerini kaydet
      await saveKioskPermissionsToFirebase();
      
      // SQL'e kiosk personel ayarlarını kaydet
      await saveKioskPersonnelSettingsToSQL();
      
      console.log(`🔄 SQL'e kiosk ayarları kaydediliyor: /api/Kiosk/${kioskId}/settings`);
      
      const kioskUpdateData = {
        companyId,
        name: currentKioskName.trim() || 'İsimsiz Kiosk',
        kioskSettings: JSON.stringify(updatedSettings)
      };
      
      console.log("📋 API'ye gönderilecek veri:", kioskUpdateData);
      console.log("🔍 kioskSettings string içeriği:", kioskUpdateData.kioskSettings.substring(0, 500));
      console.log("🔍 kioskSettings'de kioskReceiptSettings var mı?", kioskUpdateData.kioskSettings.includes('kioskReceiptSettings'));
      
      const response = await mainApi.post(`/api/Kiosk/${kioskId}/settings`, kioskUpdateData);
      const responseData = response.data;
      console.log('✅ Kiosk ayarları SQL\'e kaydedildi:', responseData);

      if (paymentSettings?.defaultWarehouseId && kioskBranchId && companyId) {
        try {
          const warehouseGetRes = await accountingApi.get(`/api/Inventory/warehouses`);
          const allWh: any[] = warehouseGetRes.data;
          const targetWarehouse = allWh.find((w: any) => w.id === paymentSettings.defaultWarehouseId);
          if (targetWarehouse && targetWarehouse.branchesId !== kioskBranchId) {
            await accountingApi.post(
              `/api/Inventory/warehouses/${paymentSettings.defaultWarehouseId}/update`,
              {
                id: paymentSettings.defaultWarehouseId,
                name: targetWarehouse.name,
                address: targetWarehouse.address,
                description: targetWarehouse.description,
                isDefault: targetWarehouse.isDefault,
                isActive: targetWarehouse.isActive ?? true,
                branchesId: kioskBranchId
              }
            );
            console.log('✅ [KIOSK-DEPO] Depo şube ilişkilendirmesi güncellendi:', paymentSettings.defaultWarehouseId, '→', kioskBranchId);
          }
        } catch (linkErr) {
          console.warn('⚠️ [KIOSK-DEPO] Depo şube ilişkilendirmesi hatası (devam edildi):', linkErr);
        }
      }

      // Başarıyla kaydedildi, initialSettings'i güncelle
      setSettings(updatedSettings);
      setInitialSettings(JSON.parse(JSON.stringify(updatedSettings)));
      
      handleClose();
    } catch (err) {
      console.error("❌ Kiosk ayarları kaydetme hatası:", err);
      setError(t('kioskSettingsModal.errors.saveFailed', { message: err instanceof Error ? err.message : t('kioskSettingsModal.errors.unknownError') }));
    } finally {
      setSaving(false);
    }
  };

  // Departman filtreleme useEffect'i
  useEffect(() => {
    if (selectedDepartment) {
      // Seçili departmana ait personelleri filtrele
      const departmentPersonnel = allPersonnel.filter(p => p.departmentId === selectedDepartment.id);
      setFilteredPersonnel(departmentPersonnel);
      console.log(`🔍 ${selectedDepartment.name} departmanında ${departmentPersonnel.length} personel bulundu`);
    } else {
      // Departman seçilmemişse tüm personelleri göster
      setFilteredPersonnel(allPersonnel);
    }
  }, [selectedDepartment, allPersonnel]);

  // Personel işlemleri için handler fonksiyonları
  const handleDepartmentSelectionChange = (newDepartment: { id: string; name: string } | null) => {
    setSelectedDepartment(newDepartment);
    
    // Departman değiştiğinde personel seçimlerini temizle
    setSelectedPersonnelIds([]);
    setSelectedPersonnelForPermissions(null);
    setPersonnelPermissionsMap({});
  };

  const handlePersonnelSelectionChange = (newSelectedIds: string[]) => {
    // Seçili personel listesinden çıkarılan personelin izinlerini sadece state'den temizle
    const removedPersonnelIds = selectedPersonnelIds.filter(id => !newSelectedIds.includes(id));
    // Yeni eklenen personelleri tespit et
    const addedPersonnelIds = newSelectedIds.filter(id => !selectedPersonnelIds.includes(id));
    
    if (removedPersonnelIds.length > 0) {
      // Sadece state'leri güncelle, Firebase/SQL'den silme işlemini kaydetme sırasında yap
      setPersonnelPermissionsMap(prev => {
        const updated = { ...prev };
        removedPersonnelIds.forEach(id => {
          delete updated[id];
        });
        return updated;
      });
      
      // Eğer şu an seçili olan personel kaldırıldıysa, seçimi temizle
      if (selectedPersonnelForPermissions && removedPersonnelIds.includes(selectedPersonnelForPermissions)) {
        setSelectedPersonnelForPermissions(null);
      }
      
      console.log(`📝 ${removedPersonnelIds.length} personel state'den kaldırıldı (Firebase/SQL'den kaydetme sırasında silinecek)`);
    }
    
    // Yeni eklenen personeller için varsayılan yetkileri oluştur
    if (addedPersonnelIds.length > 0) {
      setPersonnelPermissionsMap(prev => {
        const updated = { ...prev };
        addedPersonnelIds.forEach(id => {
          if (!updated[id]) {
            updated[id] = {
              accessKitchenScreen: true,
              viewAllOrders: true,
              updateOrderStatus: true,
              cancelOrders: false,
              showPrintButton: true,
              listReadyOrders: true,
              waiterMode: false,
              autoPrint: false,
              showPrices: true
            };
          }
        });
        return updated;
      });
      
      console.log(`✅ ${addedPersonnelIds.length} yeni personel için varsayılan yetkiler oluşturuldu`);
    }
    
    setSelectedPersonnelIds(newSelectedIds);
  };

  const handleSelectPersonnelForPermissions = (personnelId: string) => {
    setSelectedPersonnelForPermissions(personnelId);
    
    // Eğer bu personel için henüz izin ayarları yoksa, varsayılan değerleri oluştur
    if (!personnelPermissionsMap[personnelId]) {
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: {
          accessKitchenScreen: true,
          viewAllOrders: true,
          updateOrderStatus: true,
          cancelOrders: false,
          showPrintButton: true, // Varsayılan olarak yazdırma butonu göster
          listReadyOrders: true, // Varsayılan olarak hazır siparişleri listele
          waiterMode: false, // Varsayılan olarak garson modu kapalı
          autoPrint: false, // Varsayılan olarak otomatik yazdırma kapalı
          showPrices: true // Varsayılan olarak fiyatları göster
        }
      }));
    }
  };

  const handlePermissionChange = (personnelId: string, permission: string, value: boolean) => {
    setPersonnelPermissionsMap(prev => ({
      ...prev,
      [personnelId]: {
        ...prev[personnelId],
        [permission]: value
      }
    }));
  };

  // Faz 3: kiosk personeli yetkilerini SQL'e kaydet (SystemUserScreenPermissions screenType='kiosks').
  const saveKioskPermissionsToFirebase = async () => {
    if (!companyId || !kioskId) return;
    
    try {
      const items = selectedPersonnelIds.map((personnelId) => {
        const permissions = personnelPermissionsMap[personnelId] || {
          accessKitchenScreen: true,
          viewAllOrders: true,
          updateOrderStatus: true,
          cancelOrders: false,
          showPrintButton: true,
          listReadyOrders: true,
          waiterMode: false,
          autoPrint: false,
          showPrices: true
        };
        return {
          firebaseUid: personnelId,
          screenType: 'kiosks',
          screenId: String(kioskId),
          isEnabled: true,
          settings: permissions,
        };
      });
      if (items.length > 0) {
        await bulkSetScreenPermissions(items);
      }
      console.log('✅ Tüm kiosk personeli yetkileri SQL\'e kaydedildi');
    } catch (error) {
      console.error('❌ Kiosk yetkileri kaydetme hatası:', error);
      throw error;
    }
  };

  // SQL'e kiosk personel ayarlarını kaydetme fonksiyonu
  const saveKioskPersonnelSettingsToSQL = async () => {
    if (!companyId || !kioskId) return;
    
    try {
      console.log('🔄 SQL\'e kiosk personel ayarları kaydediliyor...');
      
      const personnelSettingsData = {
        assignedPersonnelIds: selectedPersonnelIds,
        personnelPermissions: personnelPermissionsMap
      };
      
      await mainApi.post(`/api/Kiosk/${kioskId}/personnel-settings`, {
        companyId,
        personnelSettings: JSON.stringify(personnelSettingsData)
      });
      
      console.log('✅ Kiosk personel ayarları SQL\'e kaydedildi');
    } catch (error) {
      console.error('❌ SQL kiosk personel ayarları kaydetme hatası:', error);
      throw error;
    }
  };

  // Faz 3: kiosk personeli yetkilerini SQL'den sil (SystemUserScreenPermissions screenType='kiosks').
  const removeKioskPersonnelFromFirebase = async (personnelIds: string[]) => {
    if (!companyId || !kioskId || personnelIds.length === 0) return;
    
    try {
      const removePromises = personnelIds.map(async (personnelId) => {
        await deleteScreenPermission(personnelId, 'kiosks', String(kioskId));
      });
      await Promise.all(removePromises);
      console.log('✅ Tüm kiosk personeli yetkileri SQL\'den silindi');
    } catch (error) {
      console.error('❌ Kiosk personeli silme hatası:', error);
      throw error;
    }
  };

  // SQL'den kiosk personeli silme fonksiyonu
  const removeKioskPersonnelFromSQL = async (personnelIds: string[]) => {
    if (!companyId || !kioskId || personnelIds.length === 0) return;
    
    try {
      console.log('🔄 SQL\'den kiosk personeli siliniyor...', personnelIds);
      
      await mainApi.post(`/api/Kiosk/${kioskId}/personnel-remove`, {
        personnelIds: personnelIds
      });
      
      console.log('✅ Kiosk personeli SQL\'den silindi');
    } catch (error) {
      console.error('❌ SQL kiosk personeli silme hatası:', error);
      throw error;
    }
  };

  // Modal kapandığında state'leri temizle
  const handleClose = () => {
    // Personel state'lerini temizle
    setSelectedPersonnelIds([]);
    setPersonnelPermissionsMap({});
    setSelectedPersonnelForPermissions(null);
    setSelectedDepartment(null);
    
    // Diğer state'leri temizle
    setSettings(null);
    setInitialSettings(null);
    setCurrentKioskName('');
    
    // Reklam state'lerini temizle
    setAdvertisements([]);
    setOrderScreenBannerAds([]);
    setAdSettingsDialogOpen(false);
    setCurrentAdDuration(5);
    setEditingAdId(null);
    setEditingAdContext('home');
    
    // Kategoriler (sadece array'i temizle, diğer state'ler KioskCategoriesTab'de)
    setCategories([]);
    
    // Ürünleri temizle
    setKioskProducts({});
    
    // Ek ayarları temizle
    setAdditionalSettings({});
    
    // Renk seçici state'lerini temizle
    setBgColorPickerOpen(false);
    setTextColorPickerOpen(false);
    setBgColorAnchorEl(null);
    setTextColorAnchorEl(null);
    
    onClose();
  };

  // Firebase Storage'a reklam resmi yükleme fonksiyonu
  const uploadAdvertisementToFirebase = async (file: File): Promise<string> => {
    if (!companyId || !kioskId) {
      throw new Error(t('kioskSettingsModal.errors.companyOrKioskIdNotFound'));
    }

    try {
      console.log('🔄 Firebase Storage\'a reklam resmi yükleniyor...', file.name);
      console.log('📦 Dosya tipi:', file.type);
      console.log('📦 Dosya boyutu:', file.size);
      
      // Benzersiz dosya adı oluştur
      const timestamp = Date.now();
      const fileName = `kiosk_ad_${timestamp}_${file.name}`;
      const filePath = `companies/${companyId}/kiosks/${kioskId}/advertisements/${fileName}`;
      
      // Firebase Storage referansı oluştur
      const fileRef = storageRef(storage, filePath);
      
      // Metadata ile dosyayı yükle (Content-Type önemli!)
      const metadata = {
        contentType: file.type || 'image/jpeg', // Dosya tipini belirt
        cacheControl: 'public,max-age=31536000', // 1 yıl cache
        customMetadata: {
          uploadedBy: 'kiosk-settings',
          uploadedAt: new Date().toISOString(),
          originalFileName: file.name
        }
      };
      
      console.log('📤 Metadata ile yükleniyor:', metadata);
      await uploadBytes(fileRef, file, metadata);
      console.log('✅ Dosya Firebase Storage\'a yüklendi');
      
      // Download URL al
      const downloadURL = await getDownloadURL(fileRef);
      console.log('✅ Download URL alındı:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Firebase Storage yükleme hatası:', error);
      throw error;
    }
  };

  // 🎬 Verilen URL'in video olup olmadığını uzantısından tespit eder.
  // Kullanıcının resim/video kütüphanesinden seçtiği medyanın tipini
  // otomatik anlamak için kullanılır (kütüphane callback'i sadece URL döndürüyor).
  const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    // Firebase Storage URL'lerinde sorgu parametreleri olabilir → path'i ayrıştır
    const cleanPath = lower.split('?')[0];
    return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/.test(cleanPath);
  };

  // Reklam ekleme/düzenleme modalını aç
  const handleOpenAdSettingsDialog = (adId?: string, context: 'home' | 'order' = 'home') => {
    setEditingAdContext(context);

    if (adId) {
      // Düzenleme modu
      const ads = context === 'home' ? advertisements : orderScreenBannerAds;
      const ad = ads.find(a => a.id === adId);
      if (ad) {
        setEditingAdId(adId);
        setCurrentAdDuration(ad.duration / 1000); // milisaniyeden saniyeye çevir
      }
    } else {
      // Yeni reklam ekleme modu
      setEditingAdId(null);
      setCurrentAdDuration(5);
    }
    setAdSettingsDialogOpen(true);
  };

  // Reklam ayarlarını kaydet
  const handleSaveAdSettings = () => {
    if (editingAdId) {
      // Düzenleme modu: Sadece süreyi güncelle, medyayı değiştirme.
      // (Mevcut item'ın mediaType/imageUrl/videoUrl alanları korunur.)
      if (editingAdContext === 'home') {
        setAdvertisements(prev => prev.map(ad =>
          ad.id === editingAdId
            ? { ...ad, duration: currentAdDuration * 1000 }
            : ad
        ));
      } else {
        setOrderScreenBannerAds(prev => prev.map(ad =>
          ad.id === editingAdId
            ? { ...ad, duration: currentAdDuration * 1000 }
            : ad
        ));
      }
      console.log(`✅ Reklam süresi güncellendi: ${editingAdId} (${editingAdContext})`);
      setAdSettingsDialogOpen(false);
      setEditingAdId(null);
      setCurrentAdDuration(5);
      return;
    }

    // 🎬 Yeni reklam ekleme — kullanıcı kendi resim/video kütüphanesinden seçer.
    // ImageLibrarySelectionModal hem resim hem video gösteriyor; dönen URL'in
    // uzantısından mediaType'ı otomatik tespit ediyoruz (Firebase upload YOK).
    setAdSettingsDialogOpen(false);
    setCurrentImageUploadCallback(() => (mediaUrl: string) => {
      const isVideo = isVideoUrl(mediaUrl);
      const newAd: Advertisement = {
        id: `ad_${Date.now()}`,
        // Video ise videoUrl, image ise imageUrl olarak ata.
        imageUrl: isVideo ? '' : mediaUrl,
        videoUrl: isVideo ? mediaUrl : undefined,
        mediaType: isVideo ? 'video' : 'image',
        order: editingAdContext === 'home'
          ? advertisements.length + 1
          : orderScreenBannerAds.length + 1,
        duration: currentAdDuration * 1000, // video için bilgi amaçlı; video kendi süresinde çalar
      };

      if (editingAdContext === 'home') {
        setAdvertisements(prev => [...prev, newAd]);
      } else {
        setOrderScreenBannerAds(prev => [...prev, newAd]);
      }

      console.log(`✅ Yeni ${isVideo ? 'VİDEO' : 'RESİM'} reklamı eklendi (${editingAdContext}):`, newAd);
      setImageLibraryModalOpen(false);
      setEditingAdId(null);
      setCurrentAdDuration(5);
    });

    setImageLibraryModalOpen(true);
  };

  // Resim kütüphanesinden resim seçildiğinde
  const handleImageLibrarySelect = (selectedUrls: string[]) => {
    if (currentImageUploadCallback && selectedUrls.length > 0) {
      // İlk resmi al (tek resim seçimi için)
      currentImageUploadCallback(selectedUrls[0]);
      setCurrentImageUploadCallback(null);
    }
  };

  // Kategori İşlemleri - Artık KioskCategoriesTab içinde yönetiliyor
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth 
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? '100vh' : '80vh' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        bgcolor: 'primary.main', 
        color: 'white', 
        py: 1, 
        px: isMobile ? 1.5 : 2,
        minHeight: isMobile ? 48 : 56
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TouchAppIcon sx={{ mr: 0.8, fontSize: isMobile ? '1rem' : '1.25rem' }}/>
          <Typography variant={isMobile ? "body2" : "subtitle1"} noWrap sx={{ maxWidth: isMobile ? 200 : 'none' }}>
            {isMobile ? t('kioskSettings.title') : t('kioskSettings.titleWithName', { name: kioskName || kioskId })}
          </Typography>
          <GuideButton guideKey="sales.kioskSettings" color="inherit" size="small" sx={{ ml: 0.5 }} />
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon fontSize="small"/>
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 116px)' : 'calc(80vh - 116px)', overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          {/* 💻 MASAÜSTÜ: Normal Tabs */}
          {!isMobile && (
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label={t('kioskSettings.aria.tabs')}
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
            >
              {tabs.map((tab) => (
                <Tab 
                  key={tab.id} 
                  icon={tab.icon} 
                  iconPosition="start"
                  label={tab.label} 
                  sx={{
                    textTransform: 'none', 
                    fontSize: '0.75rem', 
                    minHeight: 42,
                    py: 0.5,
                    px: 1.5,
                    minWidth: 'auto',
                    '& .MuiTab-iconWrapper': { fontSize: '1rem', mr: 0.5 }
                  }} 
                />
              ))}
            </Tabs>
          )}
          
          {/* 📱 MOBİL: Tab Seçici Buton */}
          {isMobile && (
            <Button
              fullWidth
              onClick={() => setMobileTabDrawerOpen(true)}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                borderRadius: 0,
                '&:hover': { bgcolor: 'primary.main' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tabs[activeTab]?.icon}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                    {t('kioskSettings.mobile.selectedSection')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {tabs[activeTab]?.label}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption">{t('kioskSettings.mobile.change')}</Typography>
                <MenuIcon fontSize="small" />
              </Box>
            </Button>
          )}
        </Box>
        
        {/* 📱 MOBİL: Tab Seçici Drawer */}
        <Drawer
          anchor="bottom"
          open={mobileTabDrawerOpen}
          onClose={() => setMobileTabDrawerOpen(false)}
          ModalProps={{ sx: { zIndex: 1400 } }}
          PaperProps={{ 
            sx: { 
              zIndex: 1401,
              borderTopLeftRadius: 16, 
              borderTopRightRadius: 16,
              maxHeight: '60vh'
            } 
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('kioskSettings.mobile.selectSection')}
            </Typography>
          </Box>
          <List sx={{ pt: 0 }}>
            {tabs.map((tab) => (
              <ListItemButton
                key={tab.id}
                selected={activeTab === tab.id}
                onClick={() => handleTabChange({} as React.SyntheticEvent, tab.id)}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' }
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: activeTab === tab.id ? 'primary.main' : 'text.secondary'
                }}>
                  {tab.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                    color: activeTab === tab.id ? 'primary.main' : 'text.primary'
                  }}
                />
                {activeTab === tab.id && (
                  <Chip label={t('kioskSettings.mobile.selectedBadge')} size="small" color="primary" variant="outlined" />
                )}
              </ListItemButton>
            ))}
          </List>
        </Drawer>

        <Box sx={{flexGrow: 1, overflow: 'auto', p: 2, height: '100%'}}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
              <CircularProgress size={30}/>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>
          ) : (
            <>
              {activeTab === 0 && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6" gutterBottom>{t('kioskSettings.advertisements.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                    {t('kioskSettings.advertisements.description')}
                  </Typography>
                  
                  {/* Ana Ekran ve Sipariş Ekranı için Ayrı Bölümler */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* ANA EKRAN REKLAMLARI */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                        {t('kioskSettings.advertisements.homeTitle')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                        {t('kioskSettings.advertisements.homeDescription')}
                      </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Sol Taraf: Reklam Listesi */}
                    <Grid item xs={12} md={7}>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {t('kioskSettings.advertisements.images')}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenAdSettingsDialog(undefined, 'home')}
                          >
                            {t('kioskSettings.advertisements.addAd')}
                          </Button>
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          {t('kioskSettings.advertisements.fullScreenInfo')}
                          <br />
                          {t('kioskSettings.advertisements.recommendedImageSize')}
                          <br />
                          {t('kioskSettings.advertisements.otherScreenSizes')}
                        </Typography>
                        
                        {advertisements.length > 0 ? (
                          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                            {advertisements.map((ad, index) => (
                              <Paper
                                key={ad.id}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2
                                }}
                              >
                                {/* Reklam Önizlemesi (image veya video) */}
                                <Box sx={{ position: 'relative', width: 80, height: 120, flexShrink: 0 }}>
                                  {ad.mediaType === 'video' && ad.videoUrl ? (
                                    <Box
                                      component="video"
                                      src={ad.videoUrl}
                                      muted
                                      playsInline
                                      preload="metadata"
                                      sx={{
                                        width: 80,
                                        height: 120,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        backgroundColor: 'black',
                                        display: 'block',
                                      }}
                                    />
                                  ) : (
                                    <Box
                                      component="img"
                                      src={ad.imageUrl}
                                      alt={`Reklam ${index + 1}`}
                                      sx={{
                                        width: 80,
                                        height: 120,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    />
                                  )}
                                  {ad.mediaType === 'video' && (
                                    <Chip
                                      icon={<MovieIcon sx={{ fontSize: '0.75rem !important' }} />}
                                      label={t('kioskSettings.advertisements.videoBadge')}
                                      size="small"
                                      color="primary"
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        left: 4,
                                        height: 20,
                                        fontSize: '0.65rem',
                                        '& .MuiChip-label': { px: 0.5 },
                                        '& .MuiChip-icon': { ml: 0.5 },
                                      }}
                                    />
                                  )}
                                </Box>

                                {/* Reklam Bilgileri */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {t('kioskSettings.advertisements.adItemTitle', { index: index + 1 })}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {ad.mediaType === 'video'
                                      ? t('kioskSettings.advertisements.videoPlaysToEnd')
                                      : t('kioskSettings.advertisements.durationSeconds', { seconds: ad.duration / 1000 })}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {t('kioskSettings.advertisements.orderLabel', { order: ad.order })}
                                  </Typography>
                                </Box>
                                
                                {/* Reklam İşlemleri */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Tooltip title={t('kioskSettings.actions.edit')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenAdSettingsDialog(ad.id, 'home')}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      // Yukarı taşı
                                      if (index > 0) {
                                        const newAds = [...advertisements];
                                        [newAds[index - 1], newAds[index]] = [newAds[index], newAds[index - 1]];
                                        newAds.forEach((a, i) => a.order = i + 1);
                                        setAdvertisements(newAds);
                                      }
                                    }}
                                    disabled={index === 0}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      // Aşağı taşı
                                      if (index < advertisements.length - 1) {
                                        const newAds = [...advertisements];
                                        [newAds[index], newAds[index + 1]] = [newAds[index + 1], newAds[index]];
                                        newAds.forEach((a, i) => a.order = i + 1);
                                        setAdvertisements(newAds);
                                      }
                                    }}
                                    disabled={index === advertisements.length - 1}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      const newAds = advertisements.filter((_, i) => i !== index);
                                      newAds.forEach((a, i) => a.order = i + 1);
                                      setAdvertisements(newAds);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('kioskSettings.advertisements.noAds')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('kioskSettings.advertisements.startByAdding')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Sağ Taraf: Buton ve Layout Ayarları */}
                    <Grid item xs={12} md={5}>
                      {/* Sipariş Ver Butonu Ayarları */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {t('kioskSettings.advertisements.orderButtonTitle')}
                        </Typography>
                        
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('kioskSettings.advertisements.buttonText')}
                            value={orderButtonSettings.text}
                            onChange={(e) => setOrderButtonSettings(prev => ({ ...prev, text: e.target.value }))}
                          />
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {t('kioskSettings.advertisements.buttonBackground')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Box
                                onClick={(e) => {
                                  setBgColorAnchorEl(e.currentTarget);
                                  setBgColorPickerOpen(!bgColorPickerOpen);
                                }}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: orderButtonSettings.backgroundColor,
                                  borderRadius: 1,
                                  border: '2px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 1
                                  }
                                }}
                              />
                              <TextField
                                size="small"
                                value={orderButtonSettings.backgroundColor}
                                onChange={(e) => setOrderButtonSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                sx={{ flex: 1 }}
                              />
                            </Box>
                            
                            {/* Arka Plan Renk Seçici Popper */}
                            <Popper 
                              open={bgColorPickerOpen} 
                              anchorEl={bgColorAnchorEl}
                              placement="bottom-start"
                              transition
                              style={{ zIndex: 1400 }}
                            >
                              {({ TransitionProps }) => (
                                <Grow {...TransitionProps}>
                                  <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                                    <ClickAwayListener onClickAway={() => setBgColorPickerOpen(false)}>
                                      <Box>
                                        <HexColorPicker
                                          color={orderButtonSettings.backgroundColor}
                                          onChange={(color) => setOrderButtonSettings(prev => ({ ...prev, backgroundColor: color }))}
                                        />
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                          <Button
                                            fullWidth
                                            size="small"
                                            onClick={async () => {
                                              try {
                                                // @ts-ignore - EyeDropper API henüz TypeScript'te yok
                                                if (window.EyeDropper) {
                                                  // @ts-ignore
                                                  const eyeDropper = new window.EyeDropper();
                                                  const result = await eyeDropper.open();
                                                  setOrderButtonSettings(prev => ({ ...prev, backgroundColor: result.sRGBHex }));
                                                } else {
                                                  alert(t('kioskSettingsModal.alerts.eyeDropperNotSupported'));
                                                }
                                              } catch (err) {
                                                console.log('Renk örnekleme iptal edildi');
                                              }
                                            }}
                                            variant="outlined"
                                            startIcon={<ColorLensIcon />}
                                          >
                                            {t('kioskSettings.colorPicker.pickColor')}
                                          </Button>
                                          <Button
                                            fullWidth
                                            size="small"
                                            onClick={() => setBgColorPickerOpen(false)}
                                            variant="outlined"
                                          >
                                            {t('kioskSettings.actions.close')}
                                          </Button>
                                        </Box>
                                      </Box>
                                    </ClickAwayListener>
                                  </Paper>
                                </Grow>
                              )}
                            </Popper>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {t('kioskSettings.advertisements.buttonTextColor')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Box
                                onClick={(e) => {
                                  setTextColorAnchorEl(e.currentTarget);
                                  setTextColorPickerOpen(!textColorPickerOpen);
                                }}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: orderButtonSettings.textColor,
                                  borderRadius: 1,
                                  border: '2px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 1
                                  }
                                }}
                              />
                              <TextField
                                size="small"
                                value={orderButtonSettings.textColor}
                                onChange={(e) => setOrderButtonSettings(prev => ({ ...prev, textColor: e.target.value }))}
                                sx={{ flex: 1 }}
                              />
                            </Box>
                            
                            {/* Yazı Renk Seçici Popper */}
                            <Popper 
                              open={textColorPickerOpen} 
                              anchorEl={textColorAnchorEl}
                              placement="bottom-start"
                              transition
                              style={{ zIndex: 1400 }}
                            >
                              {({ TransitionProps }) => (
                                <Grow {...TransitionProps}>
                                  <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                                    <ClickAwayListener onClickAway={() => setTextColorPickerOpen(false)}>
                                      <Box>
                                        <HexColorPicker
                                          color={orderButtonSettings.textColor}
                                          onChange={(color) => setOrderButtonSettings(prev => ({ ...prev, textColor: color }))}
                                        />
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                          <Button
                                            fullWidth
                                            size="small"
                                            onClick={async () => {
                                              try {
                                                // @ts-ignore - EyeDropper API henüz TypeScript'te yok
                                                if (window.EyeDropper) {
                                                  // @ts-ignore
                                                  const eyeDropper = new window.EyeDropper();
                                                  const result = await eyeDropper.open();
                                                  setOrderButtonSettings(prev => ({ ...prev, textColor: result.sRGBHex }));
                                                } else {
                                                  alert(t('kioskSettingsModal.alerts.eyeDropperNotSupported'));
                                                }
                                              } catch (err) {
                                                console.log('Renk örnekleme iptal edildi');
                                              }
                                            }}
                                            variant="outlined"
                                            startIcon={<ColorLensIcon />}
                                          >
                                            {t('kioskSettings.colorPicker.pickColor')}
                                          </Button>
                                          <Button
                                            fullWidth
                                            size="small"
                                            onClick={() => setTextColorPickerOpen(false)}
                                            variant="outlined"
                                          >
                                            {t('kioskSettings.actions.close')}
                                          </Button>
                                        </Box>
                                      </Box>
                                    </ClickAwayListener>
                                  </Paper>
                                </Grow>
                              )}
                            </Popper>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {t('kioskSettings.advertisements.fontSize', { value: orderButtonSettings.fontSize })}
                            </Typography>
                            <Slider
                              value={orderButtonSettings.fontSize}
                              onChange={(e, value) => setOrderButtonSettings(prev => ({ ...prev, fontSize: value as number }))}
                              min={16}
                              max={36}
                              step={2}
                              marks
                              valueLabelDisplay="auto"
                            />
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {t('kioskSettings.advertisements.buttonHeight', { value: orderButtonSettings.height })}
                            </Typography>
                            <Slider
                              value={orderButtonSettings.height}
                              onChange={(e, value) => setOrderButtonSettings(prev => ({ ...prev, height: value as number }))}
                              min={60}
                              max={120}
                              step={10}
                              marks
                              valueLabelDisplay="auto"
                            />
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {t('kioskSettings.advertisements.borderRadius', { value: orderButtonSettings.borderRadius })}
                            </Typography>
                            <Slider
                              value={orderButtonSettings.borderRadius}
                              onChange={(e, value) => setOrderButtonSettings(prev => ({ ...prev, borderRadius: value as number }))}
                              min={0}
                              max={30}
                              step={2}
                              marks
                              valueLabelDisplay="auto"
                            />
                          </Box>
                          
                          {/* Buton Önizlemesi */}
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                              {t('kioskSettings.advertisements.preview')}
                            </Typography>
                            <Button
                              fullWidth
                              variant="contained"
                              sx={{
                                bgcolor: orderButtonSettings.backgroundColor,
                                color: orderButtonSettings.textColor,
                                fontSize: `${orderButtonSettings.fontSize}px`,
                                height: `${orderButtonSettings.height}px`,
                                borderRadius: `${orderButtonSettings.borderRadius}px`,
                                '&:hover': {
                                  bgcolor: orderButtonSettings.backgroundColor,
                                  opacity: 0.9
                                }
                              }}
                            >
                              {orderButtonSettings.text}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                      
                      {/* Layout Ayarları */}
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {t('kioskSettings.advertisements.layout')}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {t('kioskSettings.advertisements.adArea', { value: layoutSettings.adAreaHeightPercent })}
                          </Typography>
                          <Slider
                            value={layoutSettings.adAreaHeightPercent}
                            onChange={(e, value) => {
                              const newValue = value as number;
                              setLayoutSettings({
                                adAreaHeightPercent: newValue,
                                buttonAreaHeightPercent: 100 - newValue
                              });
                            }}
                            min={50}
                            max={80}
                            step={5}
                            marks
                            valueLabelDisplay="auto"
                          />
                          
                          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mt: 2 }}>
                            {t('kioskSettings.advertisements.buttonArea', { value: layoutSettings.buttonAreaHeightPercent })}
                          </Typography>
                          <Slider
                            value={layoutSettings.buttonAreaHeightPercent}
                            onChange={(e, value) => {
                              const newValue = value as number;
                              setLayoutSettings({
                                adAreaHeightPercent: 100 - newValue,
                                buttonAreaHeightPercent: newValue
                              });
                            }}
                            min={20}
                            max={50}
                            step={5}
                            marks
                            valueLabelDisplay="auto"
                          />
                        </Box>
                        
                        {/* Ekran Boyutları Referans Tablosu */}
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            mt: 2, 
                            p: 1.5, 
                            bgcolor: 'rgba(0, 0, 0, 0.02)'
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold" gutterBottom sx={{ display: 'block', mb: 1 }}>
                            {t('kioskSettings.advertisements.fullScreenResolutionsTitle')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.8, fontSize: '0.7rem' }}>
                            {t('kioskSettings.advertisements.fullScreen21')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.8, fontSize: '0.7rem' }}>
                            {t('kioskSettings.advertisements.fullScreen24')}
                          </Typography>
                          <Typography variant="caption" color="primary" sx={{ display: 'block', lineHeight: 1.8, fontSize: '0.7rem' }}>
                            {t('kioskSettings.advertisements.fullScreen27')}
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6, fontSize: '0.65rem', fontStyle: 'italic', mt: 0.5 }}>
                            {t('kioskSettings.advertisements.fullScreenNote')}
                          </Typography>
                        </Paper>
                      </Paper>
                    </Grid>
                  </Grid>
                    </Paper>
                    
                    {/* SİPARİŞ EKRANI BANNER REKLAMLARI */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'secondary.main' }}>
                        {t('kioskSettings.advertisements.orderBannerTitle')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                        {t('kioskSettings.advertisements.orderBannerDescription')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {t('kioskSettings.advertisements.bannerAdsCount', { count: orderScreenBannerAds.length })}
                        </Typography>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenAdSettingsDialog(undefined, 'order')}
                        >
                          {t('kioskSettings.advertisements.addBanner')}
                        </Button>
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, p: 1, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 1 }}>
                        <strong>{t('kioskSettings.advertisements.bannerSizesTitle')}</strong><br />
                        {t('kioskSettings.advertisements.bannerSize27')}<br />
                        {t('kioskSettings.advertisements.bannerSize24')}<br />
                        {t('kioskSettings.advertisements.bannerSize21')}<br />
                        <em>{t('kioskSettings.advertisements.bannerSizesNote')}</em>
                      </Typography>
                      
                      {orderScreenBannerAds.length > 0 ? (
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {orderScreenBannerAds.map((ad, index) => (
                            <Paper
                              key={ad.id}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: 'rgba(156, 39, 176, 0.02)'
                              }}
                            >
                              {/* Banner Önizlemesi (image veya video) */}
                              <Box sx={{ position: 'relative', width: 160, height: 40, flexShrink: 0 }}>
                                {ad.mediaType === 'video' && ad.videoUrl ? (
                                  <Box
                                    component="video"
                                    src={ad.videoUrl}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    sx={{
                                      width: 160,
                                      height: 40,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'secondary.main',
                                      backgroundColor: 'black',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <Box
                                    component="img"
                                    src={ad.imageUrl}
                                    alt={`Banner ${index + 1}`}
                                    sx={{
                                      width: 160,
                                      height: 40,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'secondary.main',
                                    }}
                                  />
                                )}
                                {ad.mediaType === 'video' && (
                                  <Chip
                                    icon={<MovieIcon sx={{ fontSize: '0.7rem !important' }} />}
                                    label={t('kioskSettings.advertisements.videoBadge')}
                                    size="small"
                                    color="secondary"
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      left: 2,
                                      height: 18,
                                      fontSize: '0.6rem',
                                      '& .MuiChip-label': { px: 0.5 },
                                      '& .MuiChip-icon': { ml: 0.5 },
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Banner Bilgileri */}
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {t('kioskSettings.advertisements.bannerItemTitle', { index: index + 1 })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {ad.mediaType === 'video'
                                    ? t('kioskSettings.advertisements.videoPlaysToEnd')
                                    : t('kioskSettings.advertisements.durationSeconds', { seconds: ad.duration / 1000 })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {t('kioskSettings.advertisements.orderLabel', { order: ad.order })}
                                </Typography>
                              </Box>
                              
                              {/* Banner İşlemleri */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Tooltip title={t('kioskSettings.actions.edit')}>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleOpenAdSettingsDialog(ad.id, 'order')}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    // Yukarı taşı
                                    if (index > 0) {
                                      const newAds = [...orderScreenBannerAds];
                                      [newAds[index - 1], newAds[index]] = [newAds[index], newAds[index - 1]];
                                      newAds.forEach((a, i) => a.order = i + 1);
                                      setOrderScreenBannerAds(newAds);
                                    }
                                  }}
                                  disabled={index === 0}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    // Aşağı taşı
                                    if (index < orderScreenBannerAds.length - 1) {
                                      const newAds = [...orderScreenBannerAds];
                                      [newAds[index], newAds[index + 1]] = [newAds[index + 1], newAds[index]];
                                      newAds.forEach((a, i) => a.order = i + 1);
                                      setOrderScreenBannerAds(newAds);
                                    }
                                  }}
                                  disabled={index === orderScreenBannerAds.length - 1}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const newAds = orderScreenBannerAds.filter((_, i) => i !== index);
                                    newAds.forEach((a, i) => a.order = i + 1);
                                    setOrderScreenBannerAds(newAds);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 1 }}>
                          <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {t('kioskSettings.advertisements.noBannerAds')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('kioskSettings.advertisements.startByAddingBanner')}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Paper>
              )}
              
              {(activeTab === 1 || activeTab === 2) && (
                <KioskCategoryProductTemplateBar
                  companyId={companyId}
                  categories={categories}
                  kioskProducts={kioskProducts}
                  onApplyTemplate={(tpl) => {
                    setCategories(tpl.categories);
                    setKioskProducts(tpl.kioskProducts);
                  }}
                />
              )}
              {activeTab === 1 && (
                <KioskCategoriesTab
                  categories={categories}
                  companyId={companyId}
                  kioskId={kioskId}
                  onCategoriesChange={setCategories}
                  onOpenImageLibrary={(callback) => {
                    // Resim kütüphanesini aç ve callback'i kaydet
                    setCurrentImageUploadCallback(() => callback);
                    setImageLibraryModalOpen(true);
                  }}
                />
              )}
              {activeTab === 2 && (
                <KioskProductsTab
                  categories={categories}
                  products={kioskProducts}
                  onProductsChange={setKioskProducts}
                  onCategoriesChange={setCategories}
                  companyId={companyId}
                  branchId={kioskBranchId}
                  onOpenImageLibrary={(callback) => {
                    // Resim kütüphanesini aç ve callback'i kaydet
                    setCurrentImageUploadCallback(() => callback);
                    setImageLibraryModalOpen(true);
                  }}
                />
              )}
              
              {activeTab === 3 && (
                <KioskAdditionalSettingsTab
                  settings={additionalSettings}
                  onSettingsChange={setAdditionalSettings}
                />
              )}
              
              {activeTab === 4 && (
                <Paper elevation={0} sx={{p: 2, minHeight: '500px'}}>
                  <Typography variant="h6" gutterBottom>{t('kioskSettings.personnel.title')}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                    {t('kioskSettings.personnel.description')}
                  </Typography>
                  
                  {allPersonnel.length > 0 ? (
                    <Grid container spacing={2}>
                      {/* Sol Taraf: Departman ve Personel Seçimi */}
                      <Grid item xs={12} md={6}>
                        {/* Departman Seçimi */}
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('kioskSettings.personnel.departmentFilter')}
                        </Typography>
                        <Autocomplete
                          size="small"
                          id="kiosk-department-autocomplete"
                          options={departments}
                          getOptionLabel={(option) => option.name}
                          value={selectedDepartment}
                          onChange={(event, newValue) => {
                            handleDepartmentSelectionChange(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={t('kioskSettings.personnel.selectDepartmentOptional')}
                              fullWidth
                              placeholder={t('kioskSettings.personnel.allDepartments')}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontSize: '0.875rem' }}>
                              <CorporateFareIcon 
                                fontSize="small" 
                                sx={{ mr: 1, color: 'primary.main' }} 
                              />
                              {option.name}
                            </li>
                          )}
                          loading={departmentsLoading}
                          sx={{ mb: 2 }}
                          disableClearable={false}
                        />
                        
                        {/* Personel Seçimi */}
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('kioskSettings.personnel.selection')}
                          {selectedDepartment && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({selectedDepartment.name} - {filteredPersonnel.length} personel)
                            </Typography>
                          )}
                        </Typography>
                        <Autocomplete
                          multiple
                          size="small"
                          id="kiosk-personnel-autocomplete"
                          options={filteredPersonnel}
                          getOptionLabel={(option) => option.name}
                          value={filteredPersonnel.filter(p => selectedPersonnelIds.includes(p.id))}
                          onChange={(event, newValue) => {
                            const newSelectedIds = newValue.map(p => p.id);
                            handlePersonnelSelectionChange(newSelectedIds);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={selectedDepartment 
                                ? t('kioskSettingsModal.personnel.searchSelectByDept', { department: selectedDepartment.name })
                                : t('kioskSettings.personnel.searchSelect')
                              }
                              fullWidth
                            />
                          )}
                          renderTags={() => null}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ fontSize: '0.875rem' }}>
                              <Checkbox
                                size="small"
                                style={{ marginRight: 4 }}
                                checked={selected}
                              />
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2">
                                  {option.name}
                                </Typography>
                                {option.departmentName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.departmentName}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          loading={personnelLoading}
                          sx={{ mb: 2 }}
                          noOptionsText={selectedDepartment 
                            ? t('kioskSettingsModal.personnel.notFoundInDept', { department: selectedDepartment.name })
                            : t('kioskSettings.personnel.notFound')
                          }
                        />

                        {selectedPersonnelIds.length > 0 && (
                          <Box>
                            <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'medium' }}>
                              {t('kioskSettings.personnel.selectedList')}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                maxHeight: 400,
                                overflowY: 'auto'
                              }}
                            >
                              <List dense disablePadding>
                                {filteredPersonnel
                                  .filter(p => selectedPersonnelIds.includes(p.id))
                                  .map(p => (
                                    <ListItem
                                      key={p.id}
                                      selected={selectedPersonnelForPermissions === p.id}
                                      button
                                      onClick={() => handleSelectPersonnelForPermissions(p.id)}
                                      secondaryAction={
                                        <IconButton
                                          edge="end"
                                          aria-label="delete"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newSelectedIds = selectedPersonnelIds.filter(id => id !== p.id);
                                            handlePersonnelSelectionChange(newSelectedIds);
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      }
                                      divider
                                    >
                                      <ListItemText 
                                        primary={p.name}
                                        secondary={p.departmentName || t('kioskSettings.personnel.departmentUnassigned')}
                                      />
                                    </ListItem>
                                  ))}
                              </List>
                            </Paper>
                          </Box>
                        )}
                      </Grid>

                      {/* Sağ Taraf: Yetki Ayarları */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('kioskSettings.personnel.permissions')}
                        </Typography>

                        {selectedPersonnelForPermissions ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              minHeight: 200,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1
                            }}
                          >
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              {filteredPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.name || t('kioskSettings.personnel.selectedPersonnel')}
                            </Typography>
                            {selectedPersonnelForPermissions && filteredPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.departmentName && (
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                {t('kioskSettings.personnel.departmentLabel')}: {filteredPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.departmentName}
                              </Typography>
                            )}

                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.accessKitchenScreen || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'accessKitchenScreen', e.target.checked)}
                                  />
                                }
                                label={t('kioskSettings.personnel.permissionsAccess')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewAllOrders || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewAllOrders', e.target.checked)}
                                  />
                                }
                                label={t('kioskSettings.personnel.permissionsViewAll')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.updateOrderStatus || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'updateOrderStatus', e.target.checked)}
                                  />
                                }
                                label={t('kioskSettings.personnel.permissionsUpdateStatus')}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.cancelOrders || false}
                                    onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'cancelOrders', e.target.checked)}
                                  />
                                }
                                label={t('kioskSettings.personnel.permissionsCancel')}
                              />
                            </FormGroup>
                          </Paper>
                        ) : (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              minHeight: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" align="center">
                              {t('kioskSettings.personnel.selectForPermissions')}
                            </Typography>
                          </Paper>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: 'rgba(0,0,0,0.02)'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {personnelLoading ? t('kioskSettings.personnel.loading') : t('kioskSettings.personnel.empty')}
                      </Typography>
                    </Paper>
                  )}
                </Paper>
              )}
              
              {activeTab === 5 && (
                <KioskReceiptSettingsTab
                  receiptSettings={kioskReceiptSettings}
                  onReceiptSettingsChange={setKioskReceiptSettings}
                  onImageUpload={onImageUpload ? (file: File) => {
                    console.log('🔄 [KIOSK SETTINGS] onImageUpload wrapper çağrıldı');
                    return onImageUpload('kiosk-receipt-logo', file);
                  } : undefined}
                />
              )}

              {activeTab === 6 && (
                <KioskFiscalPrinterTab
                  settings={fiscalPrinterSettings}
                  onChange={setFiscalPrinterSettings}
                  companyId={companyId}
                  branchId={kioskBranchId}
                />
              )}

              {activeTab === 7 && (
                <KioskDeviceTab
                  kioskId={kioskId}
                  companyId={companyId}
                  kioskName={kioskName}
                />
              )}

              {activeTab === 8 && (
                <KioskPaymentSettingsTab
                  settings={paymentSettings}
                  onSettingsChange={setPaymentSettings}
                  companyId={companyId}
                  branchId={kioskBranchId}
                />
              )}

              {activeTab === 9 && (
                <KioskKitchenScreenTab
                  kioskId={kioskId}
                  companyId={companyId}
                  kioskBranchId={kioskBranchId}
                  currentQrMenuFirebaseId={kioskQrMenuFirebaseId}
                  onUpdated={(newFirebaseId) => setKioskQrMenuFirebaseId(newFirebaseId)}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
        <Box>
          <Button onClick={handleClose} color="inherit" disabled={saving} size="small" sx={{mr:1}}>
            {activeTab === 7 || activeTab === 9 ? t('kioskSettings.actions.close') : t('kioskSettings.actions.cancel')}
          </Button>
          {/* Cihaz Yönetimi (7) ve Mutfak Ekranı (9) tab'larında modal global Kaydet butonu gösterilmez - kendi API'sini kullanırlar */}
          {activeTab !== 7 && activeTab !== 9 && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || loading || !hasChanges}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              size="small"
            >
              {saving ? t('kioskSettings.actions.saving') : t('kioskSettings.actions.saveChanges')}
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Reklam Ayarları Modalı (Süre Ayarı) */}
      <Dialog open={adSettingsDialogOpen} onClose={() => setAdSettingsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingAdId 
            ? t('kioskSettings.advertisementDialog.editTitle', {
                context: editingAdContext === 'home'
                  ? t('kioskSettings.advertisements.homeScreen')
                  : t('kioskSettings.advertisements.orderScreen')
              })
            : t('kioskSettings.advertisementDialog.newTitle', {
                context: editingAdContext === 'home'
                  ? t('kioskSettings.advertisements.homeScreen')
                  : t('kioskSettings.advertisements.orderScreenBanner')
              })
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              {t('kioskSettings.advertisementDialog.durationLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {editingAdId
                ? t('kioskSettings.advertisementDialog.howManySeconds')
                : t('kioskSettings.advertisementDialog.howManySecondsWithContext', {
                    context: editingAdContext === 'home'
                      ? t('kioskSettings.advertisements.homeScreenLower')
                      : t('kioskSettings.advertisements.bannerLower')
                  })}
            </Typography>
            {!editingAdId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                {t('kioskSettings.advertisements.videoDurationNote')}
              </Typography>
            )}

            <Box sx={{ px: 1 }}>
              <Typography variant="h4" align="center" color={editingAdContext === 'home' ? 'primary' : 'secondary'} sx={{ mb: 1 }}>
                {t('kioskSettings.advertisementDialog.durationValue', { value: currentAdDuration })}
              </Typography>
              <Slider
                value={currentAdDuration}
                onChange={(e, value) => setCurrentAdDuration(value as number)}
                min={3}
                max={15}
                step={1}
                marks={[
                  { value: 3, label: '3s' },
                  { value: 5, label: '5s' },
                  { value: 10, label: '10s' },
                  { value: 15, label: '15s' }
                ]}
                valueLabelDisplay="auto"
                color={editingAdContext === 'home' ? 'primary' : 'secondary'}
              />
            </Box>
            
            {!editingAdId && (
              <>
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('kioskSettings.advertisementDialog.afterDurationInfo')}
                </Alert>
                
                {/* Önerilen Çözünürlük Bilgisi */}
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mt: 2, 
                    p: 1.5, 
                    bgcolor: editingAdContext === 'home' ? 'rgba(33, 150, 243, 0.05)' : 'rgba(156, 39, 176, 0.05)',
                    borderColor: editingAdContext === 'home' ? 'primary.main' : 'secondary.main',
                    borderWidth: 1
                  }}
                >
                  {editingAdContext === 'home' ? (
                    <>
                      <Typography variant="caption" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'block' }}>
                        {t('kioskSettings.advertisementDialog.fullScreenSizesTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                        {t('kioskSettings.advertisementDialog.fullScreen21')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6, mt: 0.5 }}>
                        {t('kioskSettings.advertisementDialog.fullScreen24')}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ display: 'block', lineHeight: 1.6, mt: 0.5, fontWeight: 'bold' }}>
                        {t('kioskSettings.advertisementDialog.fullScreen27')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', fontSize: '0.65rem' }}>
                        {t('kioskSettings.advertisementDialog.fullScreenNote')}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" fontWeight="bold" color="secondary" gutterBottom sx={{ display: 'block' }}>
                        {t('kioskSettings.advertisementDialog.bannerSizesTitle')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                        {t('kioskSettings.advertisementDialog.banner21')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6, mt: 0.5 }}>
                        {t('kioskSettings.advertisementDialog.banner24')}
                      </Typography>
                      <Typography variant="caption" color="secondary" sx={{ display: 'block', lineHeight: 1.6, mt: 0.5, fontWeight: 'bold' }}>
                        {t('kioskSettings.advertisementDialog.banner27')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', fontSize: '0.65rem' }}>
                        {t('kioskSettings.advertisementDialog.bannerNote')}
                      </Typography>
                    </>
                  )}
                </Paper>
              </>
            )}
            
            {editingAdId && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('kioskSettings.advertisementDialog.editOnlyDurationInfo')}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdSettingsDialogOpen(false)}>
            {t('kioskSettings.actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveAdSettings}
            variant="contained"
            color={editingAdContext === 'home' ? 'primary' : 'secondary'}
            startIcon={editingAdId ? <CheckIcon /> : <ImageIcon />}
          >
            {editingAdId ? t('kioskSettings.advertisementDialog.updateDuration') : t('kioskSettings.advertisementDialog.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim ve Video Kütüphanesi Modalı (QR Menu gibi) */}
      <ImageLibrarySelectionModal
        open={imageLibraryModalOpen}
        onClose={() => {
          setImageLibraryModalOpen(false);
          setCurrentImageUploadCallback(null);
        }}
        onSave={handleImageLibrarySelect}
        companyId={companyId || ''}
        productName="Kiosk Resmi"
        currentImageUrls={[]}
        productId={null}
      />
    </Dialog>
  );
};

export default KioskSettingsModal;
